<template lang="pug">
    bottom-modal(
        :show-close-button="true"
        id="import-override"
        data-keyboard="false"
        width=""
        :loading="onLoading('importing override entries') || onLoading('upload override entries')"
    )
        span.text-center(slot="header-text") Import Pricebook Overrides
        div.vertical-align(slot="body")
                div.col-md-offset-3.col-md-6
                    div.row
                        div.col-xs-12
                            div(v-if="!hasUploadedContent")
                                import-form(
                                    ref="importOverride",
                                    :pricebookId="pricebookId"
                                    @success="onFileUploaded",
                                    v-if="uploadProgress == 'notstarted'"
                                )
                                div.p-t-lg(v-if="uploadProgress == 'processing'")
                                    h3.text-center Processing uploaded file
                                    h3.text-center.text-muted._400.p-t-sm {{ uploadingProgress }}%

                                    .row
                                        .col-xs-12.p-t-md
                                            .progress.progress-striped
                                                .progress-bar.primary(:style="{ width: `${uploadingProgress}%`}")

                            import-mapper(
                                v-if="uploadProgress == 'uploaded' && hasUploadedContent"
                                :uploaded-content="uploadedContent"
                                :import-key="importKey"
                                :pricebookId="pricebookId"
                                ref="import"
                                @cancel="cancel"
                            )

                            div(v-if="isProcessingData")
                                include import-override-processing.pug

        button.btn.btn-fw.primary(
            v-if="!hasUploadedContent",
            slot="footer",
            @click="submit()",
            :disabled="onLoading('upload override entries')"
        ) Upload

        span(slot="footer", v-else)
            button.btn.btn-fw.white(
                @click="cancel()",
                :disabled="onLoading('importing override entries') || uploadProgress == 'validation_failed'"
            ) Back

            button.btn.btn-fw.primary.m-l-xs(
                @click="importOverride()",
                :disabled="onLoading('importing override entries') || uploadProgress == 'validation_failed'"
            )
                v-loading(loader="importing override entries") Import
</template>

<script>
import { mapActions } from 'vuex';
import importForm from './import-override-form.vue';
import importMapper from './import-override-mapper.vue';
import Auth from '@js/plugins/auth';
import ImportData, {
    UPLOAD_NOTSTARTED,
    UPLOAD_PROCESSING,
    UPLOAD_UPLOADED,
    UPLOAD_VALIDATING,
    UPLOAD_VALIDATION_FAILED,
    UPLOAD_STORING,
    UPLOAD_COMPLETED,
} from '@js/plugins/import-data';
import { Event } from '../../../../event-bus';

export default {
    components: { importForm, importMapper },

    props: {
        pricebookId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            importData: ImportData,
        };
    },

    computed: {
        hasUploadedContent() {
            return this.importData.rows.length;
        },

        uploadProgress() {
            return this.importData.uploadProgress;
        },

        importKey() {
            return this.importData.importKey;
        },

        uploadedContent() {
            return {
                rows: this.importData.rows,
                headings: this.importData.headings,
            };
        },

        uploadingProgress() {
            return this.importData.getUploadingPercentage();
        },

        isProcessingData() {
            return (
                this.uploadProgress == UPLOAD_VALIDATING ||
                this.uploadProgress == UPLOAD_VALIDATION_FAILED ||
                this.uploadProgress == UPLOAD_STORING
            );
        },
    },

    created() {
        this.importData.initEchoIfNotInitialize();
    },

    mounted() {
        this.registerModalEvent();

        Event.$on('validating-data', mapper => {
            this.importData.setUploadProgress(UPLOAD_VALIDATING);
            this.importData.setMapper(mapper);

            // Start polling timer for import validation/storage stages
            this.importData.startPolling();
        });

        this.importData.onFileUploaded(async () => {
            this.importData.setRowsAndHeadings(await this.getUploadedContent());
            this.clearLoaderState();
        });

        this.importData.onItemPassValidation(() => {
            this.processStoringData();
        });

        this.importData.onItemFailValidation(() => {
            this.clearLoaderState();
        });

        this.importData.onImportCompleted(() => {
            this.swal();
            Event.$emit('import-completed');
            this.getOverridePricebook(this.pricebookId);
            this.clearLoaderState();
            $('#import-override').modal('hide');
        });

        this.importData.onServerError(() => {
            this.swalError({ text: 'Internal error occured' });
            Event.$emit('import-error');
            this.clearLoaderState();
            $('#import-item').modal('hide');
        });

        this.importData.onPollingRequested(async () => {
            const importStatus = await this.getImportStatus();
            this.importData.syncImportStatus(importStatus);
        });

        window.onbeforeunload = e => {
            if (this.uploadProgress !== UPLOAD_NOTSTARTED) {
                e.returnValue = '';
            }
        };
    },

    methods: {
        ...mapActions('override_pricebook', ['getOverridePricebook']),

        submit() {
            this.$refs.importOverride.submit();
        },

        onFileUploaded(data) {
            this.importData.setUploadProgress(UPLOAD_PROCESSING);
            this.importData.setImportKey(data.import_key);
            this.importData.setTotalRows(data.total_rows);

            // Start polling timer for import upload stage
            this.importData.startPolling();
        },

        importOverride() {
            this.$refs.import.submit();
        },

        clearLoaderState() {
            this.endLoading('upload override entries');
            this.endLoading('importing override entries');
            this.importData.stopPolling();
        },

        cancel() {
            this.importData.reset();
            this.clearLoaderState();
        },

        registerModalEvent() {
            $('#import-override').on('hidden.bs.modal', () => {
                this.cancel();
            });
        },

        async getUploadedContent() {
            const url = Laroute.route(
                'franchise.inventory.override_pricebook.readdata',
                {
                    importKey: this.importData.importKey,
                },
            );
            const { data } = await axios.get(url);
            return data;
        },

        async processStoringData() {
            const url = Laroute.route(
                'franchise.inventory.override_pricebook.store_data',
                {
                    id: this.pricebookId,
                    importKey: this.importData.importKey,
                },
            );
            const { data } = await axios.post(url, this.importData.mapper);
            return data;
        },

        async getImportStatus() {
            const url = Laroute.route(
                'franchise.inventory.override_pricebook.import_status',
                {
                    importKey: this.importData.importKey,
                },
            );
            const { data } = await axios.get(url);

            return data;
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/inventory/override_pricebook/import/import-override.vue